import {SortingState} from '@tanstack/table-core';
import {MRT_ColumnSizingState} from 'material-react-table';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import translate from '@/utils/translate';
import EmptyState from '@/views/campaigns/components/content-submissions/empty-state';
import {ColumnDefinition} from '@/components/column-selector/ColumnSelector';
import {PROFILE_SOURCE} from '@/components/creator-profile/config/profileSources';
import {PROFILE_SOURCE_QUERY_PARAM} from '@/components/creator-profile/hooks/use-profile-source';
import useCampaignInvitedCreatorsQuery from '@/hooks/queries/use-campaign-invited-creators-query';
import useCampaignQuery from '@/hooks/queries/use-campaign-query';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useBrandId from '@/hooks/use-brand-id';
import {useDebounce} from '@/hooks/use-debounce';
import useLocalStorage from '@/hooks/use-local-storage';
import styles from '../../CampaignCreatorsTab.module.scss';
import type {InvitedCreatorRow} from '../../types/creatorTabTypes';
import {VIEW_TYPE} from '../../types/creatorTabTypes';
import invitedMatchesSortParam from '../../utils/invitedMatchesSortParam';
import CreatorsToolbar from '../creators-toolbar';
import InviteLinkModal from '../invite-link-modal';
import InvitedCreatorsTable from '../invited-creators-table';

const COLUMN_SIZING_STORAGE_KEY = 'campaign-creators-tab-invited-column-sizing';
const EMPTY_COLUMN_SIZING: MRT_ColumnSizingState = {};
const INITIAL_SORTING: SortingState = [{id: 'invitedAt', desc: true}];

function InvitedCreatorsPane() {
  const brandId = useBrandId();
  const {campaignId} = useParams<{campaignId: string}>();
  const [, setSearchParams] = useSearchParams();
  const [sorting, setSorting] = useState<SortingState>(INITIAL_SORTING);
  const invitedSort = invitedMatchesSortParam(sorting);
  const [scrollEpoch, setScrollEpoch] = useState(0);
  const [seenInvitedSort, setSeenInvitedSort] = useState(invitedSort);
  let invitedScrollKeyEpoch = scrollEpoch;
  if (invitedSort !== seenInvitedSort) {
    invitedScrollKeyEpoch = scrollEpoch + 1;
    setSeenInvitedSort(invitedSort);
    setScrollEpoch(invitedScrollKeyEpoch);
  }
  const tableScrollTopKey = `campaign-v2-invited-${campaignId}-${invitedSort}-${invitedScrollKeyEpoch}`;

  const {campaign} = useCampaignQuery({campaignId});
  const {dashboardUser} = useDashboardUserQuery({});
  const showEmailColumn = !!dashboardUser.hasPlatformAdminRole;

  const {creators, fetchNextPage, isFetching, isError, refetch} =
    useCampaignInvitedCreatorsQuery({
      brandId,
      campaignId,
      sort: invitedSort,
    });

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const toolbarColumns: ColumnDefinition[] = useMemo(() => {
    const columns: ColumnDefinition[] = [
      {id: 'creatorName', label: translate('Creator Name')},
      {id: 'invitedAt', label: translate('Date Invited')},
      {id: 'invitedStatus', label: translate('Status')},
    ];
    if (showEmailColumn) {
      columns.push({id: 'email', label: translate('Email'), canPin: true});
    }
    return columns;
  }, [showEmailColumn]);

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    () => Object.fromEntries(toolbarColumns.map((column) => [column.id, true]))
  );

  useEffect(() => {
    setVisibleColumns((prev) => {
      const next = {...prev};
      toolbarColumns.forEach((column) => {
        if (next[column.id] === undefined) {
          next[column.id] = true;
        }
      });
      return next;
    });
  }, [toolbarColumns]);

  const [persistedColumnSizing, persistColumnSizing] =
    useLocalStorage<MRT_ColumnSizingState>(
      COLUMN_SIZING_STORAGE_KEY,
      EMPTY_COLUMN_SIZING
    );
  const [columnSizing, setColumnSizing] = useState(persistedColumnSizing);
  const debouncedColumnSizing = useDebounce(columnSizing, 300);
  const persistColumnSizingRef = useRef(persistColumnSizing);
  persistColumnSizingRef.current = persistColumnSizing;
  const columnSizingRef = useRef(columnSizing);
  columnSizingRef.current = columnSizing;

  useEffect(() => {
    persistColumnSizingRef.current(debouncedColumnSizing);
  }, [debouncedColumnSizing]);

  useEffect(() => {
    return () => {
      persistColumnSizingRef.current(columnSizingRef.current);
    };
  }, []);

  const handleOpenProfile = useCallback(
    (row: InvitedCreatorRow) => {
      if (!row.profileId) {
        return;
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('creatorProfileId', row.profileId);
          next.set(PROFILE_SOURCE_QUERY_PARAM, PROFILE_SOURCE.INVITED);
          return next;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  const emptyState = isError ? (
    <EmptyState
      title={translate('Something went wrong')}
      action={{
        label: translate('Try again'),
        onClick: () => {
          Promise.resolve(refetch()).catch(() => undefined);
        },
      }}
    />
  ) : (
    <EmptyState
      title={translate("Looks like you haven't invited anyone yet.")}
    />
  );

  return (
    <div className={`${styles.pane} ${styles.content}`}>
      <CreatorsToolbar
        searchQuery=""
        onSearchChange={() => undefined}
        showingCount={creators.length}
        onInviteClick={() => setIsInviteModalOpen(true)}
        columns={toolbarColumns}
        visibleColumns={visibleColumns}
        onColumnVisibilityChange={(columnId, visible) => {
          setVisibleColumns({...visibleColumns, [columnId]: visible});
        }}
        activeCreatorsViewMode={VIEW_TYPE.Table}
        onCreatorsViewModeChange={() => undefined}
        showViewToggle={false}
        showSearch={false}
        showShare={false}
      />
      <div className={styles.tableWrapper}>
        <InvitedCreatorsTable
          data={creators}
          sorting={sorting}
          onSorting={setSorting}
          onOpenProfile={handleOpenProfile}
          onEndReached={fetchNextPage}
          isLoading={isFetching}
          emptyStateComponent={emptyState}
          columnSizing={columnSizing}
          onColumnSizingChange={setColumnSizing}
          showEmailColumn={showEmailColumn}
          tableScrollTopKey={tableScrollTopKey}
          visibleColumns={visibleColumns}
        />
      </div>
      <InviteLinkModal
        open={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        campaignName={campaign?.name ?? ''}
        inviteLink={campaign?.directLinkBriefUrl ?? ''}
      />
    </div>
  );
}

export default InvitedCreatorsPane;
